<template>
<div v-if="modal.isVisible" class="modal-overlay">
      <div 
        class="modal-content"
        :style="{
          // Dynamic border color based on type
          'border-top': '4px solid ' + (
            modal.type === 'success' ? '#10b981' :         // green-500
            (modal.type === 'error' || modal.type === 'failure' ? '#ef4444' : '#3b82f6') // red-500 or blue-500
          )
        }"
      >
        <h3 class="modal-title" 
            :style="{
              // Dynamic text color based on type
              'color': (modal.type === 'success' ? '#059669' : (modal.type === 'error' || modal.type === 'failure' ? '#dc2626' : '#1d4ed8')) 
            }">
          {{ modal.title }}
        </h3>
        <p class="modal-message">{{ modal.message }}</p>
        <div class="modal-actions">
          <button @click="hideModal" class="modal-button">
            Close
          </button>
        </div>
      </div>
    </div>
    


  <div class="container">
    <section class="user-details-section">
      <h3 class="section-title">Your Contact Information</h3>
      
      <p class="form-instruction">
        Please provide your details to finalize the appointment for 
        **{{ finalBooking.serviceName }}** on **{{ finalBooking.displayDate }}** from **{{ finalBooking.displayTime }}**.
      </p>
      
      <form @submit.prevent="submitFinalBooking" class="booking-form-grid">
        
        <div class="form-group full-width">
          <label for="user-name" class="form-label">Full Name</label>
          <input 
            type="text" 
            id="user-name" 
            v-model="user.name" 
            class="form-input" 
            required
          >
        </div>
        
        <div class="form-group full-width">
          <label for="user-phone" class="form-label">Phone Number</label>
          <input 
            type="tel" 
            id="user-phone" 
            v-model="user.phone" 
            class="form-input" 
            required
          >
        </div>

        <div class="form-group full-width">
          <label for="user-email" class="form-label">Email Address</label>
          <input 
            type="email" 
            id="user-email" 
            v-model="user.email" 
            class="form-input" 
            required
          >
        </div>

        <div class="action-buttons-group full-width">
          <button 
            type="button" 
            class="btn-secondary" 
            @click="$emit('go-back-to-dt')"
            :disabled="isLoading"
          >
            Change Date/Time
          </button>
          
          <button 
            type="submit" 
            class="submit-btn" 
            :disabled="isLoading" 
            >
                <span v-if="isLoading">Processing...</span>
                <span v-else>Confirm Booking</span>
          </button>
        </div>
      </form>
    </section>
  </div>
</template>

<script>
import { useReCaptcha } from 'vue-recaptcha-v3';

export default {
    name: 'ServiceForm',
    props: {
        finalBooking: {
            type: Object,
            required: true
        }
    },
    emits: ['go-back-to-dt', 'complete-booking'],
    computed: {
        isFormValid() {
            return this.user.name && this.user.phone && this.user.email;
        }
    },
    data() {
        return {
            user: {
                name: '',
                phone: '',
                email: ''
            },
            isLoading: false,

            // Custom Modal State
            modal: {
                isVisible: false,
                title: '',
                message: '',
                type: 'info' // success, error, info, failure
            },
        };
    },
    
    setup() {
        
        const recaptcha = useReCaptcha();
        
        if (!recaptcha || typeof recaptcha.executeRecaptcha !== 'function') {
            console.error("reCAPTCHA context not found. Ensure VueRecaptchaPlugin is installed correctly in main.js.");
            
            // Return a mock function to prevent the destructuring crash and subsequent method calls
            return {
                executeRecaptcha: () => {
                    return Promise.reject("Recaptcha Not Ready");
                }
            };
        }
        console.log("It worked")
        // Destructure only if the object is confirmed to exist
        const { executeRecaptcha } = recaptcha;
        return { executeRecaptcha }
    },
        
    methods: {
        // Modal Handlers
        showModal(title, message, type = 'info') {
            this.modal = { isVisible: true, title, message, type };
        },
        hideModal() {
            this.modal.isVisible = false;
            // Optionally clear data after closing modal
            this.modal.title = '';
            this.modal.message = '';
        },

        async submitFinalBooking() {
            this.isLoading = true;
            // Generating hte reCAPTCHA token using an action specific to this form
            let recaptchaToken = null
            try {
                // 'payment_form' is the action name that will appear in Google
                recaptchaToken = await this.executeRecaptcha('payment_form')
            } catch(error) {
                this.isLoading = false
                this.showModal('Security Error', 'Verification failed. Please refresh.', 'error')
                return
            }
            if (!recaptchaToken) {
                this.isLoading = false;
                return;
            }
           
            const finalData = {
                ...this.finalBooking,
                user: this.user,
                recaptcha_token: recaptchaToken //including the recaptcha token for backend
            };

            
            // Emit the complete data object up to the parent component
            this.$emit('complete-booking', finalData);
            this.isLoading = false
        }
    }
}
</script>

<style scoped>

 .modal-overlay {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: rgba(31, 41, 55, 0.75); /* bg-gray-800 bg-opacity-75 */
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 50; /* z-50 */
        padding: 1rem; /* p-4 */
    }

    .modal-content {
        background-color: #fff; /* bg-white */
        border-radius: 0.75rem; /* rounded-xl */
        box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); /* shadow-2xl */
        padding: 1.5rem; /* p-6 */
        width: 100%; /* w-full */
        max-width: 28rem; /* max-w-md */
        transition: transform 0.3s ease-in-out; /* transition-all duration-300 transform */
    }

    .modal-title {
        font-size: 1.25rem; /* text-xl */
        font-weight: 700; /* font-bold */
        margin-bottom: 0.75rem; /* mb-3 */
    }

    .modal-message {
        color: #4b5563; /* text-gray-700 */
        margin-bottom: 1.5rem; /* mb-6 */
        white-space: pre-line; /* whitespace-pre-line */
    }

    .modal-actions {
        display: flex; /* flex */
        justify-content: flex-end; /* justify-end */
    }

    .modal-button {
        padding: 0.5rem 1rem; /* px-4 py-2 */
        background-color: #2563eb; /* bg-blue-600 */
        color: #fff; /* text-white */
        font-weight: 500; /* font-medium */
        border-radius: 0.5rem; /* rounded-lg */
        transition: background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out; /* transition duration-150 */
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1); /* shadow-md */
        border: none;
        cursor: pointer;
    }

    .modal-button:hover {
        background-color: #1e40af; /* hover:bg-blue-700 */
    }
  

/*Layout and Centralization*/
.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
    font-family: var(--font-sans);
}

.user-details-section {
    max-width: 850px; /* Slightly wider */
    margin: 2.5rem auto 4rem; /* More vertical spacing */
    padding: 0 20px;
}

.section-title {
    font-family: var(--font-serif);
    font-size: 2.2rem;
    color: var(--color-maroon); /* Maroon for primary heading */
    margin-bottom: 30px;
    text-align: center;
    border-bottom: 2px solid var(--color-gold); /* Gold underline */
    padding-bottom: 10px;
}

.form-instruction {
    text-align: center;
    color: var(--color-dark); /* Darker text for instruction */
    margin-bottom: 35px;
    font-size: 1.15rem; /* Slightly larger */
    line-height: 1.6;
}

/*Form Grid and Equal Fields */
.booking-form-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 25px; /* Increased gap */
}

.form-group {
    position: relative;
    border: 2px solid var(--color-earth); /* Earth tone border */
    border-radius: 10px; /* More rounded */
    padding: 20px 20px 15px 20px;
    background-color: #fffaf0; /* Light gold background */
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.full-width {
    width: 100%;
}

.form-input {
    width: 100%; 
    padding: 10px 0;
    border: none;
    border-bottom: 1px solid var(--color-gold); /* Gold underline for input */
    background-color: transparent; /* Ensure background is transparent */
    font-size: 1.1rem;
    color: var(--color-dark);
    outline: none;
    transition: border-color 0.3s;
}

.form-input:focus {
    border-color: var(--color-maroon); /* Maroon on focus */
}

/* Desktop Layout (50/50 split for phone/emai)*/
@media (min-width: 650px) {
    .booking-form-grid {
        justify-content: space-between;
    }
    
    /*to split phone/email on desktop,  */
    /* .form-field-equal { 
        width: calc(50% - 12.5px); 
    } */
}

/*Action Buttons Styling*/
.action-buttons-group {
    width: 100%;
    display: flex;
    flex-direction: column; /* Stacked on mobile */
    gap: 15px;
    margin-top: 30px;
    padding-top: 20px;
    border-top: 1px solid #eee;
}

.submit-btn,
.btn-secondary {
    padding: 15px 25px;
    border-radius: 25px; /* Pill shape for elegance */
    font-size: 1.2rem;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s, transform 0.2s;
    width: 100%;
}

@media (min-width: 650px) {
    .action-buttons-group {
        flex-direction: row; /* Side-by-side on desktop */
        justify-content: flex-end;
    }
    .submit-btn,
    .btn-secondary {
        flex-grow: 0;
        width: auto;
        min-width: 200px;
    }
}

.submit-btn {
    background-color: var(--color-maroon);
    color: white;
    border: none;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.submit-btn:hover {
    background-color: #4a0c19; /* Darker Maroon */
    transform: translateY(-1px);
}

.btn-secondary {
    background-color: var(--color-gold);
    color: var(--color-dark);
    border: none; /* Gold buttons usually don't need borders */
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.btn-secondary:hover {
    background-color: #f7a000; /* Darker Gold */
    color: var(--color-maroon);
    transform: translateY(-1px);
}

.form-label {
    position: absolute;
    top: -12px;
    left: 15px; /* Match form-group padding */
    background-color: #fffaf0; /* Match form-group background */
    padding: 0 8px;
    font-size: 1rem;
    color: var(--color-earth); /* Earth tone for labels */
    font-weight: 700;
}
</style>